import { useEffect, useState } from 'react';
import { Check, RefreshCw, Sparkles, X } from 'lucide-react';

/** The bundle this tab is running (Vite puts a content hash in the file name). */
const current = () => [...document.querySelectorAll('script[type="module"][src]')].map((s) => (s as HTMLScriptElement).src.split('/').pop()).find((x) => x?.startsWith('index-'));

export interface ReleaseNote { id: string; version?: string; title: string; body?: string; highlights?: string[] }
const SEEN = 'ck.release.seen';
const seen = () => { try { return localStorage.getItem(SEEN); } catch { return null; } };
const markSeen = (id: string) => { try { localStorage.setItem(SEEN, id); } catch { /* ok */ } };

/** The pop-up itself, also used as the live preview in the CRM. */
export function UpdatePopup({ note, onUpdate, onLater, preview }: { note: ReleaseNote | null; onUpdate: () => void; onLater: () => void; preview?: boolean }) {
  const hl = note?.highlights?.filter(Boolean) ?? [];
  const card = (
    <div className="upd" role="dialog" aria-modal={!preview} aria-label="New update">
      {!preview && <button className="upd-x" onClick={onLater} aria-label="Close"><X /></button>}
      <span className="upd-ic" aria-hidden="true"><Sparkles /></span>
      <span className="upd-tag">NEW UPDATE{note?.version ? ` · ${note.version}` : ''}</span>
      <h2>{note?.title || 'Something new is here'}</h2>
      <p>{note?.body || 'We’ve added fresh features and made things faster. Update to enjoy the latest version.'}</p>
      {hl.length > 0 && <ul className="upd-hl">{hl.map((x, i) => <li key={i}><Check aria-hidden="true" />{x}</li>)}</ul>}
      <div className="upd-b"><button className="upd-go" onClick={onUpdate}><RefreshCw aria-hidden="true" /> Update now</button><button className="upd-later" onClick={onLater}>Later</button></div>
    </div>
  );
  return preview ? card : <div className="upd-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onLater(); }}>{card}</div>;
}

/** Tells people when a newer version is out (new build, or a release note published in the CRM). */
export function UpdateNotice({ latest }: { latest?: () => Promise<ReleaseNote | null> }) {
  const [build, setBuild] = useState(false);
  const [note, setNote] = useState<ReleaseNote | null>(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const mine = current();
    let stop = false;
    const check = async () => {
      if (mine) try {
        const html = await fetch(`${location.origin}/index.html`, { cache: 'no-store' }).then((r) => r.text());
        const live = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/.exec(html)?.[1];
        if (!stop && live && live !== mine) setBuild(true);
      } catch { /* offline: try again later */ }
      if (latest) try { const n = await latest(); if (!stop && n) setNote(n); } catch { /* ignore */ }
    };
    void check();
    const t = setInterval(check, 3 * 60_000);
    const onFocus = () => { void check(); };
    window.addEventListener('focus', onFocus);
    return () => { stop = true; clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, []);
  const unseenNote = !!note && seen() !== note.id;
  if (hidden || (!build && !unseenNote)) return null;
  return <UpdatePopup note={note && (unseenNote || build) ? note : null}
    onUpdate={() => { if (note) markSeen(note.id); location.reload(); }}
    onLater={() => { if (note) markSeen(note.id); setHidden(true); }} />;
}
